import { z } from "zod";
export var PolicyType;
(function (PolicyType) {
    PolicyType["DefaultPolicy"] = "policy-type_default-policy";
    PolicyType["FamilyViolencePolicy"] = "policy-type_family-violence";
    PolicyType["EnvironmentalSustainabilityPolicy"] = "policy-type_environmental-sustainability";
    PolicyType["EntityLevelGenderEqualityActionPlan"] = "policy-type_entity-level-gender-equality-action-plan";
    PolicyType["ProjectLevelGenderEqualityActionPlan"] = "policy-type_project-level-gender-equality-action-plan";
    PolicyType["FairJobsCode"] = "policy-type_fair-jobs-code";
})(PolicyType || (PolicyType = {}));
/**
 * What kind of thing this is, and therefore its shape.
 *
 * `Document` is answered by uploading a file, and may carry a blank template
 * for contractors to fill in. Every other category is a form: it carries
 * questions and has no template, because there is nothing to hand out.
 *
 * Distinct from `PolicyType`, which names a *specific* policy (Family
 * Violence Policy, Fair Jobs Code). A "Conflict of Interest Attestation" is a
 * Form whose name is free text — the two are different axes.
 */
export var PolicyCategory;
(function (PolicyCategory) {
    PolicyCategory["Document"] = "policy-category_document";
    PolicyCategory["Form"] = "policy-category_form";
    PolicyCategory["Survey"] = "policy-category_survey";
    PolicyCategory["Register"] = "policy-category_register";
})(PolicyCategory || (PolicyCategory = {}));
/** Everything except a Document is a form, and forms carry questions. */
export const policyCategoryIsForm = (category) => category !== PolicyCategory.Document;
/**
 * What a Register asks its one question about: one row per employee, or one
 * row per package. Labelled "Register for" in the UI.
 *
 * Together with the policy's level it decides who answers and which rows they
 * answer — see `listRegisterRows` in the backend:
 *
 * | Level   | Subject  | Answered by                      | One row per                        |
 * | ------- | -------- | -------------------------------- | ---------------------------------- |
 * | Project | Employee | the head contractor package      | employee assigned to the project   |
 * | Project | Package  | every package with child packages | direct child package               |
 * | Package | Employee | each package holding it          | employee assigned to that package  |
 * | Package | Package  | each package holding it          | the package itself                 |
 *
 * Fixed once the register is saved: answers are keyed on the subject, so
 * changing it would orphan every one of them. A different subject is a new
 * register.
 */
export var RegisterSubject;
(function (RegisterSubject) {
    RegisterSubject["Employee"] = "register-subject_employee";
    RegisterSubject["Package"] = "register-subject_package";
})(RegisterSubject || (RegisterSubject = {}));
/**
 * A project-level register about packages. Unlike every other project-level
 * policy, it is not owed by the head contractor alone: each package that has
 * child packages answers it for its own children, so the obligation cascades
 * down the package tree.
 */
export const policyIsCascadeRegister = (policy) => policy.policy_category === PolicyCategory.Register &&
    policy.register_subject === RegisterSubject.Package &&
    Boolean(policy.is_project_level_policy);
/**
 * Whether a package owes a policy at all — before its frequency decides which
 * periods. The rule the report's due list applies.
 *
 * Documents analytics (`owingPackages`) apply the same cascade for registers,
 * but differ in one place that predates registers: they count the head
 * contractor for a package-level policy only when it holds it, where this
 * makes it owe every policy. Aligning them would change existing figures, so
 * it is left for its own change.
 *
 * - The head contractor package owes every policy on the project.
 * - Any other package owes the package-level policies it holds.
 * - A project-level policy is the head contractor's alone — except a register
 *   about packages, which cascades: a package that holds it *and* has child
 *   packages answers it for those children.
 */
export const packageOwesPolicy = (policy, pkg) => {
    if (pkg.isHeadContractor) {
        return true;
    }
    if (policyIsCascadeRegister(policy)) {
        return pkg.holdsPolicy && pkg.hasChildren;
    }
    return !policy.is_project_level_policy && pkg.holdsPolicy;
};
export var PolicyFrequency;
(function (PolicyFrequency) {
    PolicyFrequency["OneTime"] = "policy-frequency_one-time";
    PolicyFrequency["Monthly"] = "policy-frequency_monthly";
    PolicyFrequency["Quarterly"] = "policy-frequency_quarterly";
    PolicyFrequency["HalfYearly"] = "policy-frequency_half-yearly";
    PolicyFrequency["Annually"] = "policy-frequency_annually";
})(PolicyFrequency || (PolicyFrequency = {}));
export const projectPolicySchema = z.object({
    created: z.date(),
    frequency: z.enum(PolicyFrequency),
    generation_source: z.custom().optional(),
    /**
     * A form carries questions; a document is answered by a single upload.
     *
     * Set as a side effect of adding the first question rather than chosen up
     * front — there is no mode switch in the UI. It exists as an explicit column
     * so that archiving the last question cannot silently turn a form back into
     * a document, which would change a contractor's obligation mid-period.
     */
    is_form: z.boolean().optional(),
    is_project_level_policy: z.boolean(),
    policy_category: z.enum(PolicyCategory).optional(),
    policy_description: z.string().optional(),
    policy_type: z.enum(PolicyType),
    project_id: z.string(),
    project_policy_id: z.string(),
    /**
     * Carried on the policy so that a project save persists both in one call,
     * with the questions inserted after their parent — the same shape as
     * `project.policies`.
     */
    questions: z.array(z.custom()).optional(),
    /** Registers only, and then required — see `RegisterSubject`. */
    register_subject: z.enum(RegisterSubject).optional(),
    source_framework_id: z.string().optional(),
    source_framework_target_id: z.string().optional(),
    uri: z.string().optional()
});
export const policyFrequencyToString = (service) => {
    switch (service) {
        case PolicyFrequency.OneTime:
            return "One Time";
        case PolicyFrequency.Monthly:
            return "Monthly";
        case PolicyFrequency.Quarterly:
            return "Quarterly";
        case PolicyFrequency.HalfYearly:
            return "Half-yearly";
        case PolicyFrequency.Annually:
            return "Annually";
        default:
            return "";
    }
};
